'use client'

import { useMapboxContext } from '@component/mapbox/map/context'
import MapboxMarkerBase from '../baseMarker'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { findIndex, get, isFunction, map, size } from 'lodash-es'
import classNames from 'classnames'
import { useBoolean } from 'react-use'
import useFetch from '@hooks/useFetch'
import { isEstimateData, isRVData, isSoldData } from '@utils/house'
import { joinStr, subscribeUI, unSubscribeUI } from '@utils'
import ListingsMap, { ListingsMapSkeleton } from '@component/card/listings/map'
import { statsMapPoint } from '@plugin/statistics'

function getPopupOffset(size) {
  return {
    top: [0, size],
    bottom: [0, -size],
    left: [size, 0],
    right: [-size, 0],
  }
}
export default function HouseBaseMarker({
  data,
  textZoom = 19,
  onClick,
  active = false,
  onAdded,
  onPopupLoaded,
  onPopupLeave,
  className,
  isForSale = false,
  isRent = false,
  isSold = false,
  isEstimate = false,
  isRv = false,
}) {

  const { mapZoom } = useMapboxContext()
  const showText = useMemo(() => mapZoom >= textZoom, [textZoom, mapZoom])
  const text = useMemo(() => {
    let res = get(data, 'priceLabel')
    if (isSoldData(data)) {
      res = data.soldPrice || 'Sold'
    }
    if (isEstimateData(data) || isRVData(data)) {
      switch (data.type) {
        case 'avm':
          res = `${data.avm}*`
          break
        case 'sold':
          res = data.soldPrice || 'Sold'
          break
        case 'rv':
          res = data.rv
          break
      }
    }
    return res
  }, [data])
  const count = useMemo(() => {
    const res = size(data.combineIds)
    return res > 1 ? res : undefined
  }, [data])
  const [opened, toggleOpen] = useBoolean(false)
  const [popup, setPopup] = useState()
  const [marker, setMarker] = useState()
  const { lat, lng } = data

  // 处理鼠标移入移出显示隐藏
  useMouseMarkerPopup(marker, popup, onPopupLeave)

  useEffect(() => {
    if (!popup) return

    const onOpen = () => toggleOpen(true)
    const onClose = () => toggleOpen(false)
    popup.on('open', onOpen)
    popup.on('close', onClose)

    return () => {
      popup.off('open', onOpen)
      popup.off('close', onClose)
    }
  }, [popup, toggleOpen, data])

  const [hoverData, setHoverData] = useState()
  const clickHandle = useCallback(() => isFunction(onClick) && onClick(data), [onClick, data])
  const addedHandle = useCallback(marker => {
    marker.data = data
    marker.setHoverData = d => setHoverData(d)
    isFunction(onAdded) && onAdded(marker)
    setMarker(marker)
  }, [data, onAdded, setHoverData, setMarker])

  const mouseEnterHandle = useCallback(event => event.target.style.zIndex = 1, [])
  const mouseLeaveHandle = useCallback(event => event.target.style.zIndex = '', [])

  return (
    <MapboxMarkerBase
      lng={lng}
      lat={lat}
      id={data.id}
      onAdded={addedHandle}
      onAddedPopup={setPopup}
      onMouseEnter={mouseEnterHandle}
      onMouseLeave={mouseLeaveHandle}
      popup={
        <MarkerPopup
          data={data}
          opened={opened}
          hoverData={hoverData}
          isForSale={isForSale}
          isRent={isRent}
          isSold={isSold}
          isEstimate={isEstimate}
          isRv={isRv}
          onLoaded={onPopupLoaded}
        ></MarkerPopup>
      }
      popupOps={{ offset: getPopupOffset(showText ? 18 : 10) }}
    >
      <div className='p-4 sm:p-0'>
        <div className={classNames(
          'border-2 border-white cursor-pointer transition-all ease-out relative hover:bg-font drop-shadow-lg',
          showText ? 'px-8 py-2 rounded-xl text-white text-md hover:-translate-y-2 hover:scale-y-105' : 'rounded-half',
          { '!bg-font': active || opened },
          { 'text-xs text-white flex items-center justify-center': count },
          !showText && (count ? 'w-22 h-22 hover:w-26 hover:h-26' : 'w-14 h-14 hover:w-20 hover:h-20'),
          !showText && opened && (count ? '!w-26 !h-26' : '!w-20 !h-20'),
          { '-translate-y-2 scale-y-105': showText && opened },
          className
        )} onClick={clickHandle} id={data.id} data-a={data.houseId}>
          {
            showText
              ? text
              : count
          }
          {showText && <i className='absolute border-[7px] border-transparent border-t-white border-b-0 left-1/2 top-full -translate-x-1/2'></i>}
        </div>
      </div>
    </MapboxMarkerBase>
  )
}

// hook：鼠标移动到marker上显示popup，移除延时200毫秒隐藏，如果移入了popup则不隐藏，移除popup隐藏
function useMouseMarkerPopup(marker, popup, onMouseLeave) {

  useEffect(() => {
    if (!marker || !popup) return

    var timer

    const openPopup = () => {
      if (!popup.isOpen()) marker.togglePopup()
    }
    const closePopup = () => {
      if (popup.isOpen()) marker.togglePopup()
      isFunction(onMouseLeave) && onMouseLeave()
    }

    const markerMouseEnter = () => {
      clearTimeout(timer)
      openPopup()

      const popupEl = popup.getElement()
      subscribeUI('mouseenter', markerMouseEnter, popupEl)
      subscribeUI('mouseleave', markerMouseLeave, popupEl)
    }
    const markerMouseLeave = () => {
      timer = setTimeout(() => {
        closePopup()
      }, 180)
    }

    const markerEl = marker.getElement()
    const popupEl = popup.getElement()

    subscribeUI('mouseenter', markerMouseEnter, markerEl)
    subscribeUI('mouseleave', markerMouseLeave, markerEl)
    subscribeUI('mouseenter', markerMouseEnter, popupEl)
    subscribeUI('mouseleave', markerMouseLeave, popupEl)

    return () => {
      unSubscribeUI('mouseenter', markerMouseEnter, markerEl)
      unSubscribeUI('mouseleave', markerMouseLeave, markerEl)
      unSubscribeUI('mouseenter', markerMouseEnter, popupEl)
      unSubscribeUI('mouseleave', markerMouseLeave, popupEl)
    }
  }, [marker, popup, onMouseLeave])
}


function MarkerPopup({
  data,
  opened = false,
  hoverData,
  isForSale = false,
  isRent = false,
  isSold = false,
  isEstimate,
  isRv,
  onLoaded
}) {

  const hoverIndex = useMemo(() => findIndex(data.combineIds, item => item === get(hoverData, 'id')), [hoverData, data])
  const points = useMemo(() => {
    if (!size(data.combineTypeIds)) return
    return map(data.combineTypeIds, item => joinStr(item, '_'))
  }, [data])
  const { data: list, isLoading } = useFetch(opened && points ? `map-points/${points}` : void 0, {
    onSuccess(data) {
      statsMapPoint(joinStr(map(data, 'id')), isSold)
    }
  })

  useEffect(() => {
    if (opened && size(list) > 0) {
      isFunction(onLoaded) && onLoaded(list)
    }
  }, [list, opened, onLoaded])
  return (
    <>
      {
        isLoading && (
          <ListingsMapSkeleton></ListingsMapSkeleton>
        )
      }
      {
        list && !isLoading && (
          <div className='-mx-10 -mt-10 -mb-15'>
            <ListingsMap list={list} showIndex={hoverIndex} isForSale={isForSale} isRent={isRent} isSold={isSold} isEstimate={isEstimate} isRv={isRv}></ListingsMap>
          </div>
        )
      }
    </>
  )
}